import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import FeatureIdeaCard from "./FeatureIdeaCard";
import SubmitFeatureIdeaModal from "./SubmitFeatureIdeaModal";
import Pagination from "./Pagination";
import {
    FeatureIdeaStatus,
    FeatureIdeaSummary,
    fetchFeatureIdeas,
} from "../services/featureRequestsService";
import { isAdmin, isAthlete, isCoach, isStaff } from "../utils/auth";
import "../styles/feature-requests.css";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];

export default function FeatureRequestsPage() {
    if (isAthlete() || (!isAdmin() && !isStaff() && !isCoach())) {
        return <Navigate to="/athlete/profile" replace />;
    }

    return <FeatureRequestsPageInner />;
}

function FeatureRequestsPageInner() {
    const [tab, setTab] = useState<FeatureIdeaStatus>("ACTIVE");
    const [ideas, setIdeas] = useState<FeatureIdeaSummary[]>([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const skipNextLoadRef = useRef(false);

    const load = useCallback(async (status: FeatureIdeaStatus, pageNum: number, size: number) => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchFeatureIdeas(status, pageNum, size);
            setIdeas(data.content);
            setTotalPages(data.totalPages);
            setTotalElements(data.totalElements);
            setPage(data.number);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to load discussions.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (skipNextLoadRef.current) {
            skipNextLoadRef.current = false;
            return;
        }
        void load(tab, page, pageSize);
    }, [load, tab, page, pageSize]);

    const handleTabChange = (next: FeatureIdeaStatus) => {
        setTab(next);
        setPage(0);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setPage(0);
    };

    const handleIdeaSubmitted = useCallback(async (created: FeatureIdeaSummary) => {
        // Common path: already on Active page 1 — insert locally, no reload.
        if (tab === "ACTIVE" && page === 0) {
            setIdeas((prev) =>
                [created, ...prev.filter((idea) => idea.id !== created.id)].slice(0, pageSize),
            );
            setTotalElements((prev) => {
                const next = prev + 1;
                setTotalPages(Math.max(1, Math.ceil(next / pageSize)));
                return next;
            });
            return;
        }

        // Coming from Completed or another page: switch to Active page 1 without a loading flash.
        skipNextLoadRef.current = true;
        setTab("ACTIVE");
        setPage(0);
        setError(null);
        setLoading(false);
        try {
            const data = await fetchFeatureIdeas("ACTIVE", 0, pageSize);
            const alreadyPresent = data.content.some((idea) => idea.id === created.id);
            const merged = [
                created,
                ...data.content.filter((idea) => idea.id !== created.id),
            ].slice(0, pageSize);
            setIdeas(merged);
            const nextTotal = alreadyPresent ? data.totalElements : data.totalElements + 1;
            setTotalElements(nextTotal);
            setTotalPages(Math.max(1, Math.ceil(nextTotal / pageSize)));
        } catch {
            setIdeas([created]);
            setTotalElements(1);
            setTotalPages(1);
        }
    }, [tab, page, pageSize]);

    return (
        <main className="feature-requests-page" data-testid="feature-requests-page">
            <div className="feature-requests-header page-bar">
                <div>
                    <h1 className="feature-requests-title">Community Forum</h1>
                    <p className="feature-requests-subtitle">
                        Vote on ideas, join discussions, and share feedback with the community. Your
                        input helps shape the future of Polaris Athlete.
                    </p>
                </div>
                <button
                    type="button"
                    className="feature-requests-submit-btn"
                    data-testid="feature-requests-open-submit"
                    onClick={() => setModalOpen(true)}
                >
                    Create a post
                </button>
            </div>

            <div className="feature-requests-tabs" role="tablist" aria-label="Discussion status">
                <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "ACTIVE"}
                    className={`feature-requests-tab${tab === "ACTIVE" ? " active" : ""}`}
                    data-testid="feature-requests-tab-active"
                    onClick={() => handleTabChange("ACTIVE")}
                >
                    Active posts
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "COMPLETED"}
                    className={`feature-requests-tab${tab === "COMPLETED" ? " active" : ""}`}
                    data-testid="feature-requests-tab-completed"
                    onClick={() => handleTabChange("COMPLETED")}
                >
                    Completed
                </button>
            </div>

            {error && (
                <div className="feature-requests-error" role="alert">
                    {error}
                </div>
            )}
            {loading && <div className="feature-requests-loading">Loading discussions…</div>}
            {!loading && ideas.length === 0 && (
                <div className="feature-requests-empty">
                    {tab === "ACTIVE"
                        ? "No active discussions yet, be the first to post."
                        : "No completed discussions yet."}
                </div>
            )}

            {!loading && ideas.length > 0 && (
                <div className="feature-idea-list">
                    {ideas.map((idea) => (
                        <FeatureIdeaCard
                            key={idea.id}
                            idea={idea}
                            onIdeaUpdated={(updated) => {
                                setIdeas((prev) =>
                                    prev.map((item) =>
                                        item.id === updated.id ? { ...item, ...updated } : item,
                                    ),
                                );
                            }}
                        />
                    ))}
                </div>
            )}

            {!loading && (
                <Pagination
                    currentPage={page}
                    totalPages={Math.max(totalPages, 1)}
                    totalElements={totalElements}
                    pageSize={pageSize}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    onPageChange={setPage}
                    onPageSizeChange={handlePageSizeChange}
                />
            )}

            <SubmitFeatureIdeaModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmitted={handleIdeaSubmitted}
            />
        </main>
    );
}
